import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  RiArrowUpSLine,
  RiArrowDownSLine,
  RiSearchLine,
  RiRefreshLine,
  RiAddLine,
  RiEditLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiFilterLine,
  RiMoreLine
} from 'react-icons/ri';
import Skeleton from './SkeletonLoader';
import SortHeaderContent from './SortHeaderContent';
import { STICKY_ACTION_HEADER, STICKY_ACTION_CELL, STICKY_SHADOW } from './tableStyles';

export interface Column {
  key: string;
  title: React.ReactNode;  // allow JSX heading content
  render?: (value: any, record: any) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  /**
   * ตรึงคอลัมน์ไว้ขอบขวา · ปล่อยว่าง = คอลัมน์ปุ่ม (key `actions`/`action`) ตรึงอัตโนมัติ
   * ใส่ `false` เพื่อยกเลิกการตรึงเป็นรายกรณี
   */
  sticky?: boolean;
}

/** คอลัมน์ปุ่มที่ต้องตรึงขอบขวาโดยไม่ต้องให้แต่ละหน้าสั่งเอง (G3) */
const ACTION_COLUMN_KEYS = ['actions', 'action'];

/**
 * คอลัมน์นี้ต้องตรึงขอบขวาไหม
 * เดิมเป็น opt-in รายหน้า ทำให้หลายหน้า (โดยเฉพาะกลุ่มใบวางบิล) ลืมใส่แล้วปุ่มถูกตัดที่จอ 1440px
 * จึงเปลี่ยนเป็น "ตรึงให้เองถ้าเป็นคอลัมน์ปุ่ม" และยังสั่งทับได้ด้วย `sticky`
 */
const isStickyColumn = (column: Column): boolean =>
  column.sticky ?? ACTION_COLUMN_KEYS.includes(column.key);

interface TableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  emptyText?: string;
  emptyMessage?: string; // alias of emptyText
  // KD3 S2048: คำแนะนำใต้ข้อความว่าง · null = ไม่แสดง (เช่นโหลดล้ม — "ลองปรับตัวกรอง" ชี้ผิดทาง)
  emptyHint?: string | null;
  pagination?: boolean;
  pageSize?: number;
  totalItems?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  onRowClick?: (record: any) => void;
  rowKey?: string;
  actions?: false | {
    view?: boolean;
    edit?: boolean;
    delete?: boolean;
    onView?: (record: any) => void;
    onEdit?: (record: any) => void;
    onDelete?: (record: any) => void;
  };
  searchable?: boolean;
  searchValue?: string;
  onSearch?: (value: string) => void;
  searchPlaceholder?: string;
  sortable?: boolean;
  defaultSortKey?: string;
  defaultSortOrder?: 'asc' | 'desc';
  onSort?: (key: string, order: 'asc' | 'desc') => void;
  toolbar?: React.ReactNode;
  onRefresh?: () => void | Promise<void>;
  onAdd?: () => void;
  selectable?: boolean;
  selectedIds?: number[];
  onSelectAll?: (selected: boolean) => void;
  onSelectRow?: (id: number, selected: boolean) => void;
  /** Accessible label for the table element */
  ariaLabel?: string;
}

const Table: React.FC<TableProps> = ({
  columns,
  data,
  loading = false,
  emptyText = 'ไม่มีข้อมูล',
  emptyHint = 'ลองปรับเงื่อนไขการค้นหาหรือตัวกรองใหม่',
  pagination = true,
  pageSize = 10,
  totalItems = 0,
  currentPage = 1,
  onPageChange,
  onRowClick,
  rowKey = 'id',
  actions,
  searchable = false,
  searchValue: externalSearchValue,
  onSearch,
  searchPlaceholder = 'ค้นหา...',
  sortable = false,
  defaultSortKey,
  defaultSortOrder = 'asc',
  onSort,
  toolbar,
  onRefresh,
  onAdd,
  selectable = false,
  selectedIds = [],
  onSelectAll,
  onSelectRow,
  ariaLabel
}) => {
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey || null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(defaultSortOrder);
  const [searchValue, setSearchValue] = useState(externalSearchValue || '');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Refs for dual scrollbar synchronization
  const topScrollRef = useRef<HTMLDivElement>(null);
  const bottomScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  // Sync searchValue with externalSearchValue
  useEffect(() => {
    if (externalSearchValue !== undefined) {
      setSearchValue(externalSearchValue);
    }
  }, [externalSearchValue]);

  // Update table width for top scrollbar
  useEffect(() => {
    const updateTableWidth = () => {
      if (tableRef.current) {
        setTableWidth(tableRef.current.scrollWidth);
      }
    };

    updateTableWidth();

    // Use ResizeObserver to track table width changes
    const resizeObserver = new ResizeObserver(updateTableWidth);
    if (tableRef.current) {
      resizeObserver.observe(tableRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [data, columns]);

  // Scroll sync handlers
  const handleTopScroll = useCallback(() => {
    if (isScrolling) return;
    setIsScrolling(true);
    if (bottomScrollRef.current && topScrollRef.current) {
      bottomScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => setIsScrolling(false));
  }, [isScrolling]);

  const handleBottomScroll = useCallback(() => {
    if (isScrolling) return;
    setIsScrolling(true);
    if (topScrollRef.current && bottomScrollRef.current) {
      topScrollRef.current.scrollLeft = bottomScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => setIsScrolling(false));
  }, [isScrolling]);

  // จัดการการเปลี่ยนหน้า
  const handlePageChange = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  // จัดการการเรียงลำดับ
  const handleSort = (key: string, columnSortable?: boolean) => {
    // อนุญาตให้ sort ถ้า table-level sortable=true หรือ column-level sortable=true
    if (!sortable && !columnSortable) return;

    let order: 'asc' | 'desc' = 'asc';
    if (sortKey === key) {
      order = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(order);
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }

    if (onSort) {
      onSort(key, order);
    }
  };

  // จัดการการค้นหา
  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchValue);
    }
  };

  // จัดการการรีเฟรชข้อมูล
  const handleRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      
      try {
        // Call onRefresh and wait if it returns a Promise
        const result = onRefresh();
        if (result && typeof result.then === 'function') {
          await result;
        }
      } catch (error) {
        console.error('Error refreshing data:', error);
      } finally {
        // Always stop the spinner after completion or error
        setIsRefreshing(false);
      }
    }
  };

  // Client-side sorting - ใช้เมื่อไม่มี onSort callback (server-side sorting)
  const sortedData = useMemo(() => {
    // ถ้าไม่มี sortKey หรือไม่มีข้อมูล ใช้ data เดิม
    if (!sortKey || !data || data.length === 0) {
      return data;
    }

    // ถ้ามี onSort callback หมายความว่าใช้ server-side sorting ไม่ต้อง sort ฝั่ง client
    if (onSort) {
      return data;
    }

    // Client-side sorting
    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
      if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

      // Compare values
      let comparison = 0;
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        // String comparison (case-insensitive)
        comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase(), 'th');
      } else if (aValue instanceof Date && bValue instanceof Date) {
        // Date comparison
        comparison = aValue.getTime() - bValue.getTime();
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        // Number comparison
        comparison = aValue - bValue;
      } else {
        // Try to parse as date string first
        const dateA = Date.parse(aValue);
        const dateB = Date.parse(bValue);
        if (!isNaN(dateA) && !isNaN(dateB)) {
          comparison = dateA - dateB;
        } else {
          // Default string comparison
          comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'th');
        }
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  }, [data, sortKey, sortOrder, onSort]);

  // คำนวณจำนวนหน้าทั้งหมด
  const totalPages = Math.ceil(totalItems / pageSize);

  // สร้างปุ่มเปลี่ยนหน้า
  const renderPagination = () => {
    // ให้แสดง pagination เสมอเมื่อมีข้อมูลและกำหนดให้มี pagination
    if (!pagination || (totalItems === 0 && !loading)) return null;

    const pageButtons = [];
    const maxPageButtons = 5; // จำนวนปุ่มเลขหน้าที่แสดง
    
    // คำนวณช่วงของปุ่มที่จะแสดง
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    // ปรับช่วงถ้าจำเป็น
    if (endPage - startPage + 1 < maxPageButtons && startPage > 1) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    // ปุ่มหน้าแรก
    pageButtons.push(
      <button
        key="first"
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        className="flex items-center justify-center w-8 h-8 rounded-md text-xs text-secondary-500 hover:bg-secondary-200 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="หน้าแรก"
      >
        «
      </button>
    );

    // ปุ่มหน้าก่อนหน้า
    pageButtons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="flex items-center justify-center w-8 h-8 rounded-md text-xs text-secondary-500 hover:bg-secondary-200 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="หน้าก่อนหน้า"
      >
        ‹
      </button>
    );

    // ปุ่มเลขหน้า
    for (let i = startPage; i <= endPage; i++) {
      pageButtons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`flex items-center justify-center min-w-[32px] h-8 px-2 rounded-md text-xs font-medium transition-colors ${
            currentPage === i
              ? 'bg-primary-600 text-white shadow-sm'
              : 'text-secondary-600 hover:bg-secondary-200'
          }`}
        >
          {i}
        </button>
      );
    }

    // ปุ่มหน้าถัดไป
    pageButtons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="flex items-center justify-center w-8 h-8 rounded-md text-xs text-secondary-500 hover:bg-secondary-200 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="หน้าถัดไป"
      >
        ›
      </button>
    );

    // ปุ่มหน้าสุดท้าย
    pageButtons.push(
      <button
        key="last"
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="flex items-center justify-center w-8 h-8 rounded-md text-xs text-secondary-500 hover:bg-secondary-200 hover:text-secondary-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        aria-label="หน้าสุดท้าย"
      >
        »
      </button>
    );

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-secondary-200 bg-secondary-50/50 gap-2">
        {/* KD3 S2494 (NFR-MD-012): ค้นหา/กรองแล้วจำนวนผลลัพธ์เปลี่ยน แต่ screen reader ไม่รู้ต้องกวาดตารางเอง → ประกาศแบบ polite */}
        <div className="text-xs text-secondary-600" role="status" aria-live="polite" aria-atomic="true">
          แสดง{' '}
          <span className="font-semibold text-secondary-600">
            {totalItems > 0 ? Math.min((currentPage - 1) * pageSize + 1, totalItems) : 0}–{Math.min(currentPage * pageSize, totalItems)}
          </span>{' '}
          จากทั้งหมด{' '}
          <span className="font-semibold text-secondary-600">{totalItems.toLocaleString()}</span>{' '}
          รายการ
        </div>
        <div className="flex items-center gap-0.5">{pageButtons}</div>
      </div>
    );
  };

  // จัดการ checkbox select all
  const handleCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onSelectAll) {
      onSelectAll(e.target.checked);
    }
  };

  // จัดการ checkbox select row
  const handleCheckRow = (id: number, checked: boolean) => {
    if (onSelectRow) {
      onSelectRow(id, checked);
    }
  };

  // ตรวจสอบว่าเลือกทั้งหมดหรือไม่
  const isAllSelected = useMemo(() => {
    if (!selectable || !data || data.length === 0) return false;
    return data.every((record) => selectedIds?.includes(record[rowKey]));
  }, [selectable, data, selectedIds, rowKey]);

  // ตรวจสอบว่ามีบางตัวที่เลือกหรือไม่ (indeterminate)
  const isIndeterminate = useMemo(() => {
    if (!selectable || !data || data.length === 0) return false;
    const selectedCount = data.filter((record) => selectedIds?.includes(record[rowKey])).length;
    return selectedCount > 0 && selectedCount < data.length;
  }, [selectable, data, selectedIds, rowKey]);

  // สร้างส่วนหัวของตาราง
  const renderTableHeader = () => {
    return (
      <thead className="bg-secondary-50 border-b border-secondary-200">
        <tr>
          {/* Checkbox column */}
          {selectable && (
            <th
              scope="col"
              className="px-3 sm:px-4 py-3.5 text-center text-xs font-medium text-secondary-500 w-10"
            >
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = isIndeterminate;
                  }
                }}
                onChange={handleCheckAll}
                className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 cursor-pointer"
                aria-label="เลือกทั้งหมด"
              />
            </th>
          )}
          {columns.map((column) => {
            // ตรวจสอบว่า column นี้สามารถ sort ได้หรือไม่
            // KD3 S2514 (NFR-MD-027): คอลัมน์ checkbox เลือกแถว (key 'select') ห้ามเป็นปุ่ม sort —
            // เดิม sortable ระดับตารางห่อ checkbox ไว้ในปุ่ม (axe nested-interactive) และกด "เลือกทั้งหมด"
            // แล้วหัวคอลัมน์ถูกสั่ง sort ตามไปด้วย · ทุกหน้าใช้ key นี้ ไม่มีหน้าไหนตั้ง sortable:false เอง
            const isColumnSortable =
              column.key !== 'select' &&
              (column.sortable === true || (sortable && column.sortable !== false));

            return (
              <th
                key={column.key}
                scope="col"
                className={`px-3 sm:px-4 py-3.5 text-left text-xs font-semibold text-secondary-500 tracking-normal whitespace-nowrap ${
                  isColumnSortable ? 'cursor-pointer hover:bg-secondary-100 hover:text-secondary-700 select-none transition-colors' : ''
                } ${
                  isStickyColumn(column) ? STICKY_ACTION_HEADER : ''
                }`}
                style={{ width: column.width }}
                // BUG-S2281-002: หัวคอลัมน์ที่เรียงได้ต้องใช้คีย์บอร์ดได้ + บอกสถานะการเรียงให้ screen reader
                aria-sort={
                  isColumnSortable
                    ? sortKey === column.key
                      ? sortOrder === 'asc' ? 'ascending' : 'descending'
                      : 'none'
                    : undefined
                }
              >
                <SortHeaderContent
                  sortable={isColumnSortable}
                  onSort={() => handleSort(column.key, column.sortable)}
                >
                  <span>{column.title}</span>
                  {isColumnSortable && sortKey === column.key && (
                    <span className="ml-1">
                      {sortOrder === 'asc' ? (
                        <RiArrowUpSLine className="w-4 h-4" />
                      ) : (
                        <RiArrowDownSLine className="w-4 h-4" />
                      )}
                    </span>
                  )}
                  {/* แสดง icon เพื่อบอกว่าสามารถ sort ได้ */}
                  {isColumnSortable && sortKey !== column.key && (
                    <span className="ml-1 opacity-30">
                      <RiArrowUpSLine className="w-4 h-4" />
                    </span>
                  )}
                </SortHeaderContent>
              </th>
            );
          })}
          {actions && (actions.view || actions.edit || actions.delete) && (
            <th
              scope="col"
              className={`px-3 sm:px-4 py-3.5 text-center text-xs font-semibold text-secondary-500 w-20 sm:w-24 ${STICKY_ACTION_HEADER}`}
            >
              <span className="hidden sm:inline">การจัดการ</span>
              <span className="sm:hidden">จัดการ</span>
            </th>
          )}
        </tr>
      </thead>
    );
  };

  // สร้างส่วนเนื้อหาของตาราง
  const renderTableBody = () => {
    if (loading) {
      return (
        <tbody className="bg-white divide-y divide-secondary-100">
          {Array.from({ length: Math.min(pageSize, 5) }).map((_, index) => (
            <tr key={`skeleton-${index}`} className={index % 2 === 1 ? 'bg-secondary-50/30' : ''}>
              {selectable && (
                <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap text-center">
                  <Skeleton width="16px" height="16px" />
                </td>
              )}
              {columns.map((column) => (
                <td key={column.key} className="px-3 sm:px-4 py-3.5 whitespace-nowrap">
                  <Skeleton />
                </td>
              ))}
              {actions && (actions.view || actions.edit || actions.delete) && (
                <td className="px-3 sm:px-4 py-3.5 whitespace-nowrap text-center">
                  <div className="flex items-center justify-center gap-1.5">
                    <Skeleton width="28px" height="28px" />
                    <Skeleton width="28px" height="28px" />
                    <Skeleton width="28px" height="28px" />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      );
    }

    if (!sortedData || sortedData.length === 0) {
      return (
        <tbody className="bg-white divide-y divide-secondary-100">
          <tr>
            <td
              colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)}
              className="px-6 py-16 text-center"
            >
              <div className="flex flex-col items-center justify-center gap-2">
                <div className="w-14 h-14 rounded-2xl bg-secondary-100 flex items-center justify-center mb-2">
                  <RiSearchLine className="w-7 h-7 text-secondary-300" />
                </div>
                <p className="text-sm font-medium text-secondary-600">{emptyText}</p>
                {emptyHint && <p className="text-xs text-secondary-600">{emptyHint}</p>}
              </div>
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody className="bg-white divide-y divide-secondary-100">
        {(sortedData || []).map((record, index) => {
          // Ensure record is valid and has required properties
          if (!record || typeof record !== 'object') {
            return null;
          }

          // Use fallback key if rowKey is missing
          const recordKey = record[rowKey] != null ? record[rowKey] : `row-${index}`;
          const isSelected = selectedIds?.includes(record[rowKey]);

          return (
            <tr
              key={recordKey}
              className={`group transition-colors duration-100 ${
                isSelected
                  ? 'bg-primary-50 hover:bg-primary-100/60'
                  : 'hover:bg-secondary-50'
              } ${onRowClick ? 'cursor-pointer' : ''}`}
              onClick={() => onRowClick && onRowClick(record)}
            >
              {/* Checkbox column */}
              {selectable && (
                <td
                  className="px-3 sm:px-4 py-3 whitespace-nowrap text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => handleCheckRow(record[rowKey], e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500 cursor-pointer"
                    aria-label="เลือกรายการ"
                  />
                </td>
              )}
              {columns.map((column) => {
                // Safely get column value with null check
                const columnValue = record && record[column.key] != null ? record[column.key] : '';

                return (
                  <td
                    key={column.key}
                    className={`px-3 sm:px-4 py-3 whitespace-nowrap text-sm text-secondary-700 ${
                      isStickyColumn(column)
                        ? `sticky right-0 z-10 ${STICKY_SHADOW} ${isSelected ? 'bg-primary-50' : 'bg-white group-hover:bg-secondary-50/70'}`
                        : ''
                    }`}
                  >
                    {column.render
                      ? column.render(columnValue, record)
                      : columnValue || <span className="text-secondary-300">—</span>}
                  </td>
                );
              })}
            {actions && (actions.view || actions.edit || actions.delete) && (
              <td className={`px-3 sm:px-4 py-3 whitespace-nowrap text-center sticky right-0 z-10 ${STICKY_SHADOW} ${isSelected ? 'bg-primary-50' : 'bg-white group-hover:bg-secondary-50/70'}`}>
                <div className="flex items-center justify-center gap-1">
                  {actions.view && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.onView && record && actions.onView(record);
                      }}
                      className="p-1.5 rounded-lg text-primary-600 hover:bg-primary-100 hover:text-primary-700 transition-colors"
                      title="ดูรายละเอียด"
                      aria-label="ดูรายละเอียด"
                    >
                      <RiEyeLine className="w-4 h-4" />
                    </button>
                  )}
                  {actions.edit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.onEdit && record && actions.onEdit(record);
                      }}
                      className="p-1.5 rounded-lg text-success-600 hover:bg-success-100 hover:text-success-700 transition-colors"
                      title="แก้ไข"
                      aria-label="แก้ไข"
                    >
                      <RiEditLine className="w-4 h-4" />
                    </button>
                  )}
                  {actions.delete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        actions.onDelete && record && actions.onDelete(record);
                      }}
                      className="p-1.5 rounded-lg text-danger-500 hover:bg-danger-100 hover:text-danger-700 transition-colors"
                      title="ลบ"
                      aria-label="ลบ"
                    >
                      <RiDeleteBinLine className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            )}
          </tr>
          );
        })}
      </tbody>
    );
  };

  // สร้างส่วน toolbar ของตาราง
  const renderToolbar = () => {
    return (
      <div className="flex flex-col gap-3 mb-4">
        {/* แถวบน: ปุ่มและ custom toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
          {/* ด้านซ้าย: ปุ่มเพิ่มข้อมูลและรีเฟรช */}
          <div className="flex flex-wrap items-center gap-2">
            {onAdd && (
              <button
                onClick={onAdd}
                className="inline-flex items-center h-9 px-3.5 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors gap-1.5"
              >
                <RiAddLine className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">เพิ่มข้อมูล</span>
                <span className="sm:hidden">เพิ่ม</span>
              </button>
            )}
            {onRefresh && (
              <button
                onClick={handleRefresh}
                className="inline-flex items-center justify-center h-9 w-9 border border-secondary-300 shadow-sm text-sm font-medium rounded-lg text-secondary-600 bg-white hover:bg-secondary-50 hover:text-secondary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                disabled={isRefreshing}
              >
                <RiRefreshLine className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="sr-only">รีเฟรช</span>
              </button>
            )}
          </div>

          {/* custom toolbar */}
          {toolbar && (
            <div className="flex-1 sm:flex-none">
              {toolbar}
            </div>
          )}
        </div>

        {/* แถวล่าง: ช่องค้นหา */}
        {searchable && (
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div className="relative rounded-lg shadow-sm flex-1 sm:max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                <RiSearchLine className="h-5 w-5 text-secondary-400" />
              </div>
              <input
                type="text"
                className="h-10 w-full pl-10 pr-10 border border-secondary-300 rounded-lg bg-white text-sm focus:ring-primary-500 focus:border-primary-500"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              {searchValue && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    onClick={() => {
                      setSearchValue('');
                      onSearch && onSearch('');
                    }}
                    className="p-1 rounded-full text-secondary-400 hover:text-secondary-500"
                    aria-label="ล้างคำค้นหา"
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="inline-flex items-center justify-center h-10 px-4 border border-secondary-300 shadow-sm text-sm font-medium rounded-md text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              ค้นหา
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Toolbar */}
      {(searchable || onRefresh || onAdd || toolbar) && renderToolbar()}

      {/* ตาราง - ใช้ responsive design สำหรับมือถือ */}
      <div className="bg-white border border-secondary-200 rounded-xl overflow-hidden shadow-sm">
        {/* Top Scrollbar - แสดงเฉพาะเมื่อ table มี horizontal scroll */}
        {tableWidth > 0 && (
          <div
            ref={topScrollRef}
            onScroll={handleTopScroll}
            className="overflow-x-auto overflow-y-hidden border-b border-secondary-200 bg-secondary-50"
            style={{ height: '10px' }}
          >
            <div style={{ width: tableWidth, height: '1px' }} />
          </div>
        )}

        <div
          ref={bottomScrollRef}
          onScroll={handleBottomScroll}
          tabIndex={0}
          role="region"
          aria-label={ariaLabel || "ตารางข้อมูล"}
          className="overflow-x-auto"
        >
          <table
            ref={tableRef}
            className="min-w-full divide-y divide-secondary-200 text-xs sm:text-sm"
            aria-label={ariaLabel || "ตารางข้อมูล"}
          >
            {renderTableHeader()}
            {renderTableBody()}
          </table>
        </div>
        {/* Pagination - อยู่ภายใน card เดียวกัน */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default Table;